// markdown מסונן (11 §1): בלי HTML גולמי (מוצג כטקסט), בלי קישורים חיצוניים — רק נתיבים יחסיים.
import { Marked, type RendererObject, type Tokens } from 'marked';
import DOMPurify from 'dompurify';

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const renderer: RendererObject = {
  html(t: Tokens.HTML | Tokens.Tag) {
    return escapeHtml(t.text);
  },
  link(t: Tokens.Link) {
    const text = this.parser.parseInline(t.tokens);
    const internal = /^\/(?!\/)/.test(t.href) || t.href.startsWith('#');
    return internal ? `<a href="${escapeHtml(t.href)}">${text}</a>` : `<span class="ab-link-text">${text}</span>`;
  },
  image(t: Tokens.Image) {
    return escapeHtml(t.text);
  },
};

const md = new Marked({ gfm: true, breaks: false, renderer });
/** breaks — שורה חדשה בודדת = שבירת שורה (טקסט שיחה, כמו שהמודל כותב) */
const mdBreaks = new Marked({ gfm: true, breaks: true, renderer });

const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'a', 'span'];

/** כיוון לפי התו החזק הראשון (כמו dir="auto"), או null כשאין. */
function firstStrongDir(text: string): 'rtl' | 'ltr' | null {
  const m = text.match(/[A-Za-z\u00C0-\u024F\u0370-\u03FF\u0400-\u04FF\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/);
  if (!m) return null;
  return /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(m[0]) ? 'rtl' : 'ltr';
}

/**
 * כיוון לכל בלוק (עברית ואנגלית מעורבבות בלי "קפיצות"): פסקה/כותרת/תא — dir="auto"; רשימה וציטוט — כיוון מפורש לפי
 * הטקסט שלהם, ופריטים/פסקאות בתוכם יורשים (dir="auto" על הרשימה לא רואה טקסט של צאצא עם dir משלו, והסימונים היו
 * עוברים לצד הלא נכון).
 */
function applyDirs(root: DocumentFragment) {
  for (const el of root.querySelectorAll('ul, ol, blockquote')) {
    if (el.parentElement?.closest('ul, ol, blockquote')) continue;
    const d = firstStrongDir(el.textContent ?? '');
    if (d) el.setAttribute('dir', d);
  }
  for (const el of root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, td, th')) if (!el.closest('ul, ol, blockquote')) el.setAttribute('dir', 'auto');
}

/** markdown → HTML מסונן. breaks — שורה חדשה = <br>; dirAuto — כיוון לכל בלוק (applyDirs), אחרי הסינון. */
export function renderMarkdown(src: string, o: { breaks?: boolean; dirAuto?: boolean } = {}): string {
  const html = (o.breaks ? mdBreaks : md).parse(src, { async: false }) as string;
  const cfg = { ALLOWED_TAGS, ALLOWED_ATTR: ['href', 'class', 'align', 'start'] };
  if (!o.dirAuto) return DOMPurify.sanitize(html, cfg);
  const frag = DOMPurify.sanitize(html, { ...cfg, RETURN_DOM_FRAGMENT: true });
  applyDirs(frag);
  const box = document.createElement('div');
  box.appendChild(frag);
  return box.innerHTML;
}
